import { useExportApi } from '../UseExportApi';
import { ExportChartOptions } from '../Model';
import { useCallback } from 'react';
import { createExportFormats } from './CreateExportFormats';
import { FactRevenue, Option, PageName } from '@utils/pageConfig/Types';
import { ExportPerformanceBySegmentResp } from '@data-visualization/performance-analysis-line-chart/Types';
import { UseMultipleChartsType } from '@ecosystem/export-utils';
import { ProductsCompareExport, ProductsCompareKpi, RetailerKpiOption, SaleType } from '@models/Products';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { AveragePriceChartResp } from '@data-visualization/average-selling-price-chart/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { useGetLoadedCountries } from '../UseGetLoadedCountries';
import { useGetLoadedProductGroups } from '../UseGetLoadedProductGroups';
import { CapturedImage } from '@ecosystem/newron-design-system/build/export-utils/captureImage';
import { ExportExploreTrendsStack } from '@data-visualization/trends-stack-chart/Types';
import { SortFieldDisplayProps } from '@components/design/migrated/Table';

export interface ExportStackChartOptions extends ExportChartOptions {
  title: string;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  data: ExportExploreTrendsStack;
  stackTrendChartExcelData?: ExportExploreTrendsStack;
  timePeriod: string;
  comparisonTimePeriod: string;
  factRevenue: FactRevenue;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  selectedKpi: Option;
  chartTitles: string[];
  chartSubTitles: string[];
  multipleChartsExport?: UseMultipleChartsType;
  sortedBy: SortFieldDisplayProps;
  pageName: PageName;
  exportImage?: CapturedImage | CapturedImage[];
  volume?: string;
  selectedSalesType?: SaleType;
  selectedRetailerOption?: RetailerKpiOption; //pending
}

export interface ExportLineChartOptions extends ExportChartOptions {
  title: string;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  data: ExportPerformanceBySegmentResp | ProductsCompareKpi | ProductsCompareExport[] | AveragePriceChartResp;
  timePeriod: string;
  comparisonTimePeriod: string;
  factRevenue: FactRevenue;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  selectedKpi: Option;
  chartTitles: string[];
  chartSubTitles: string[];
  multipleChartsExport?: UseMultipleChartsType;
  pageName: PageName;
  exportImage?: CapturedImage | CapturedImage[];
  volume?: string;
  selectedSalesType?: SaleType;
  selectedRetailerOption?: RetailerKpiOption; //pending
}

export function useExportMultiChart() {
  const { exportFile: exportFn } = useExportApi();
  const pagesConfig = usePageConfig();
  const loadedCountries = useGetLoadedCountries();
  const { loadedProductGroups } = useGetLoadedProductGroups();

  const download = useCallback(
    async (options) => {
      const { volume = DEFAULT_VOLUME, multipleChartsExport, pageName, ...restOptions } = options;

      await createExportFormats({
        exportFn,
        multipleChartsExport,
        pageName,
        volume,
        pagesConfig,
        ...loadedCountries,
        ...loadedProductGroups,
        ...restOptions,
      });
    },
    [pagesConfig, loadedCountries, loadedProductGroups],
  );

  return { download };
}
